import { useRef, useState } from 'react';
import { GripVertical, ClockPlus, SquarePen, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../../../shared/components/Button';
import Tooltip from '../../../shared/components/Tooltip';
import skullIcon from '../../../shared/assets/button-icons/death.png';

function TrackerRow({
  tracker,
  mode,
  onToggleVisible,
  onEdit,
  onDelete,
  onChangeValue,
  onDragStart,
  onDragOver,
  onDrop,
  dragOverId,
}) {
  const isFear = tracker.locked;

  // For the Fear tracker in the GM view, reverse the pip order so filled pips
  // grow right-to-left (mirroring how Fear is tracked physically at the table).
  const basePips = Array.from({ length: tracker.size }, (_, i) => i);
  const displayPips = isFear ? [...basePips].reverse() : basePips;

  return (
    <div
      className={`tracker_row${mode === 'dark' ? ' tracker_row_dark' : ''}${dragOverId === tracker.id ? ' drag_over_before' : ''}`}
      draggable={!isFear}
      onDragStart={isFear ? undefined : (e) => onDragStart(e, tracker.id)}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver(e, tracker.id);
      }}
      onDrop={(e) => onDrop(e, tracker.id)}
      style={{marginTop: '0px'}}
    >
      {!isFear && (
        <span
          className={`drag_handle${isFear ? ' drag_handle_disabled' : ''}`}
          aria-hidden='true'
        >
          <GripVertical size={14} />
        </span>
      )}

      <div className='tracker_row_content'>
        <div className='tracker_pips_group'>

            <span className='tracker_name_label'>
              {isFear ? 'Fear' : tracker.name && tracker.name}
            </span>
          
          <div className='tracker_pips'>
            {displayPips.map((i) => (
              <Tooltip
                key={i}
                content={i < tracker.value ? `Clear to ${i}` : `Set to ${i + 1}`}
              >
                <button
                  className={`tracker_pip${i < tracker.value ? ' tracker_pip_filled' : ''}`}
                  onClick={() =>
                    onChangeValue(tracker.id, i < tracker.value ? i : i + 1)
                  }
                  aria-label={`${tracker.name || 'Fear'} pip ${i + 1}${i < tracker.value ? ' (filled)' : ''}`}
                >
                  {isFear ? (
                    <img
                      src={skullIcon}
                      alt=''
                      className={`tracker_skull_icon${i < tracker.value ? ' tracker_skull_filled' : ' tracker_skull_empty'} ${mode !== 'dark' ? ' tracker_skull_icon_light_mode' : ' tracker_skull_icon_dark'}`}
                    />
                  ) : (
                    <span
                      className={`tracker_circle_pip${i < tracker.value ? ' tracker_circle_pip_filled' : ''}`}
                      style={i < tracker.value ? { background: tracker.color || '#e94f37', borderColor: tracker.color || '#e94f37' } : {}}
                      aria-hidden='true'
                    />
                  )}
                </button>
              </Tooltip>
            ))}
          </div>
        </div>
      </div>

      <div className='tracker_row_actions'>
        <Button
          icon={tracker.visible ? 'hide' : 'show'}
          type={tracker.visible ? 'tomato' : 'black-white'}
          description={tracker.visible ? 'Hide from Display' : 'Show on Display'}
          onPress={() => onToggleVisible(tracker.id)}
        />
        {!isFear && (
          <>
            <Button
              icon={SquarePen}
              type='black-white'
              description='Edit tracker'
              onPress={() => onEdit(tracker)}
            />
          </>
        )}
      </div>
    </div>
  );
}

function TrackerSection({
  trackers = [],
  mode,
  onAddTracker,
  onEditTracker,
  onDeleteTracker,
  onToggleTrackerVisible,
  onChangeTrackerValue,
  onReorderTrackers,
  onToggleGroupVisible,
}) {
  const dragSourceRef = useRef(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const fearTracker = trackers.find((tracker) => tracker.locked);
  const regularTrackers = trackers.filter((tracker) => !tracker.locked);

  const toggleCollapse = (grp) => {
    setCollapsedGroups((prev) => ({ ...prev, [grp]: !prev[grp] }));
  };

  // Separate regular trackers into grouped and ungrouped
  const grouped = {};
  const ungrouped = [];
  regularTrackers.forEach((tracker) => {
    const grp = (tracker.group || '').trim();
    if (grp) {
      if (!grouped[grp]) grouped[grp] = [];
      grouped[grp].push(tracker);
    } else {
      ungrouped.push(tracker);
    }
  });

  const clearDrag = () => {
    dragSourceRef.current = null;
    setDragOverId(null);
  };

  const handleDragStart = (e, id) => {
    dragSourceRef.current = id;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, id) => {
    e.preventDefault();
    setDragOverId(id);
  };

  const handleDrop = (e, targetId, targetGroupName) => {
    e.preventDefault();
    const sourceId = dragSourceRef.current;
    clearDrag();
    if (!sourceId || sourceId === targetId) return;

    const sourceTracker = trackers.find((t) => t.id === sourceId);
    if (!sourceTracker || sourceTracker.locked) return;

    const targetTracker = targetId ? trackers.find((t) => t.id === targetId) : null;
    if (targetTracker && targetTracker.locked) return;

    const newOrder = [...trackers];
    const fromIdx = newOrder.findIndex((t) => t.id === sourceId);
    if (fromIdx === -1) return;

    // Update group assignment to target group
    const updatedSource = { ...sourceTracker, group: targetGroupName ?? (sourceTracker.group || '') };

    if (targetId != null) {
      const toIdx = newOrder.findIndex((t) => t.id === targetId);
      if (toIdx === -1) return;
      newOrder.splice(fromIdx, 1);
      newOrder.splice(toIdx, 0, updatedSource);
    } else {
      // Dropped on a group header or ungrouped zone – append to that group
      newOrder.splice(fromIdx, 1);
      const grpItems = newOrder.filter((t) => !t.locked && (t.group || '').trim() === (targetGroupName || ''));
      if (grpItems.length > 0) {
        const lastIdx = newOrder.findIndex((t) => t.id === grpItems[grpItems.length - 1].id);
        newOrder.splice(lastIdx + 1, 0, updatedSource);
      } else {
        newOrder.push(updatedSource);
      }
    }
    onReorderTrackers(newOrder);
  };

  const handleDragEnd = () => {
    clearDrag();
  };

  return (
    <div className='tracker_section' onDragEnd={handleDragEnd}>
      <div className='tracker_section_header'>
        <div className='tracker_add_button' style={{ marginRight: 4, marginTop: 7 }}>
          <Button icon={ClockPlus} description='Add Tracker' type='cinnabar' onPress={onAddTracker} />
        </div>
        {fearTracker && (
          <div className='tracker_inline_fear'>
            <TrackerRow
              tracker={fearTracker}
              mode={mode}
              onToggleVisible={onToggleTrackerVisible}
              onEdit={onEditTracker}
              onDelete={onDeleteTracker}
              onChangeValue={onChangeTrackerValue}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={(e, id) => handleDrop(e, id, '')}
              dragOverId={dragOverId}
            />
          </div>
        )}
      </div>

      {/* Grouped trackers */}
      {Object.entries(grouped).map(([grp, grpTrackers]) => {
        const isCollapsed = !!collapsedGroups[grp];
        const allVisible = grpTrackers.every((t) => t.visible !== false);
        const isGrpOver = dragOverId === `__grp_${grp}`;
        return (
          <div key={grp} className='group_section'>
            <div
              className={`group_header${isGrpOver ? ' drag_over_group' : ''}`}
              onClick={() => toggleCollapse(grp)}
              role='button'
              tabIndex={0}
              aria-expanded={!isCollapsed}
              onKeyDown={(e) => {
                if (e.key === ' ') e.preventDefault();
                if (e.key === 'Enter' || e.key === ' ') toggleCollapse(grp);
              }}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverId(`__grp_${grp}`); }}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(e, null, grp); }}
            >
              <button className='group_collapse_btn' tabIndex={-1} aria-hidden='true'>
                {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
              </button>
              <span className='group_name'>{grp}</span>
              <div onClick={(e) => e.stopPropagation()}>
                <Button
                  icon={allVisible ? 'hide' : 'show'}
                  type={allVisible ? 'tomato' : 'black-white'}
                  description={allVisible ? 'Hide Group' : 'Show Group'}
                  onPress={() => onToggleGroupVisible && onToggleGroupVisible(grp)}
                />
              </div>
            </div>
            {!isCollapsed && (
              <div
                className='group_items'
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(e, null, grp); }}
              >
                {grpTrackers.map((tracker) => (
                  <TrackerRow
                    key={tracker.id}
                    tracker={tracker}
                    mode={mode}
                    onToggleVisible={onToggleTrackerVisible}
                    onEdit={onEditTracker}
                    onDelete={onDeleteTracker}
                    onChangeValue={onChangeTrackerValue}
                    onDragStart={handleDragStart}
                    onDragOver={(e, id) => handleDragOver(e, id)}
                    onDrop={(e, id) => handleDrop(e, id, grp)}
                    dragOverId={dragOverId}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Ungrouped trackers */}
      <div
        className='ungrouped_zone'
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(e, null, ''); }}
      >
        {ungrouped.map((tracker) => (
          <TrackerRow
            key={tracker.id}
            tracker={tracker}
            mode={mode}
            onToggleVisible={onToggleTrackerVisible}
            onEdit={onEditTracker}
            onDelete={onDeleteTracker}
            onChangeValue={onChangeTrackerValue}
            onDragStart={handleDragStart}
            onDragOver={(e, id) => handleDragOver(e, id)}
            onDrop={(e, id) => handleDrop(e, id, '')}
            dragOverId={dragOverId}
          />
        ))}
      </div>
    </div>
  );
}

export default TrackerSection;
